import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { ArrowUpRight, Pin, PinOff, Sparkles, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import useCommandCenterData from '@/hooks/useCommandCenterData';
import useStatusPulse from '@/hooks/useStatusPulse';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { track } from '@/services/analytics';
import {
  SIDEBAR_HEADER,
  SIDEBAR_FOCUS,
  SIDEBAR_NAV_GROUPS,
  SIDEBAR_SHORTCUTS,
  SIDEBAR_SUPPORT,
  SIDEBAR_STATUS
} from '@/constants/sidebarSchema';

const BACKDROP_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 0.4 }
};

const SIDEBAR_VARIANTS = {
  hidden: { x: -320, opacity: 0 },
  visible: { x: 0, opacity: 1 }
};

const SYNC_STATUS_COPY = {
  synced: {
    label: 'Healthy',
    helper: 'All devices up to date',
    className: 'text-emerald-600'
  },
  syncing: {
    label: 'Syncing',
    helper: 'Pushing pending changes',
    className: 'text-sky-600'
  },
  local: {
    label: 'Offline mode',
    helper: 'Working from local cache',
    className: 'text-amber-600'
  }
};

function CommandCenterContent({ currentUser, commandData, syncStatus, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPinned, togglePin, closeSidebar } = useSidebar();
  const [signingOut, setSigningOut] = useState(false);

  const firstName = currentUser?.displayName?.split(' ')[0] ?? 'FinList member';
  const {
    loading: dataLoading,
    error: dataError,
    refresh,
    listStats = {},
    goalStats = {},
    financeStats = {},
    checklist = []
  } = commandData || {};
  const {
    pulse: platformPulse,
    loading: statusLoading,
    error: statusError,
    refresh: refreshStatus,
    meta: statusMeta
  } = useStatusPulse();
  const lastStatusIndicatorRef = useRef(null);

  const handleNavigate = useCallback((item) => {
    if (!item?.path) return;
    navigate(item.path, item.state ? { state: item.state } : undefined);
    if (!isPinned) {
      closeSidebar();
    }
  }, [navigate, closeSidebar, isPinned]);

  const handleSupportLink = useCallback((link) => {
    if (!link) return;
    if (link.path) {
      handleNavigate(link);
      return;
    }

    if (link.href) {
      window.open(link.href, '_blank', 'noopener,noreferrer');
    }
  }, [handleNavigate]);

  const handleConciergeEmail = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.location.href = `mailto:${SIDEBAR_SUPPORT.concierge.email}`;
  }, []);

  const handleSignOut = useCallback(async () => {
    if (!onLogout || signingOut) return;
    setSigningOut(true);
    try {
      await onLogout();
      track('sidebar_support_action_completed', { id: 'signout', outcome: 'success' }).catch(() => {});
      closeSidebar();
      // RULE 1: Manual sign-out redirects to /welcome (NOT /signin)
      navigate('/welcome', { replace: true });
    } catch (error) {
      track('sidebar_support_action_completed', {
        id: 'signout',
        outcome: 'error',
        message: error?.message
      }).catch(() => {});
      console.error('Unable to sign out from sidebar', error);
    } finally {
      setSigningOut(false);
    }
  }, [onLogout, signingOut, closeSidebar, navigate]);

  const handleSupportAction = useCallback((action) => {
    if (!action) return;

    track('sidebar_support_action_selected', { id: action.id, type: action.type }).catch(() => {});

    if (action.type === 'signout') {
      handleSignOut();
      return;
    }

    if (action.type === 'email' && action.email && typeof window !== 'undefined') {
      window.location.href = `mailto:${action.email}`;
      return;
    }

    handleSupportLink(action);
  }, [handleSignOut, handleSupportLink]);

  const isActivePath = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const shortcutAccents = [
    'bg-emerald-500/10 text-emerald-600',
    'bg-amber-500/10 text-amber-600',
    'bg-rose-500/10 text-rose-600',
    'bg-sky-500/10 text-sky-600'
  ];
  const supportActionTone = {
    default: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
    accent: 'bg-brand-500/10 text-brand-600',
    danger: 'bg-rose-500/10 text-rose-600',
    success: 'bg-emerald-500/10 text-emerald-600',
    warning: 'bg-amber-500/10 text-amber-600',
    muted: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-300'
  };
  const statusSeverityStyles = {
    success: { chip: 'bg-emerald-500/10 text-emerald-600', dot: 'bg-emerald-400', text: 'text-emerald-600' },
    warning: { chip: 'bg-amber-500/10 text-amber-600', dot: 'bg-amber-400', text: 'text-amber-600' },
    danger: { chip: 'bg-rose-500/10 text-rose-600', dot: 'bg-rose-500', text: 'text-rose-600' },
    accent: { chip: 'bg-sky-500/10 text-sky-600', dot: 'bg-sky-400', text: 'text-sky-600' },
    muted: { chip: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-300', dot: 'bg-gray-400', text: 'text-gray-500 dark:text-gray-300' }
  };
  const statusAction = SIDEBAR_SUPPORT.actions?.find((action) => action.id === 'status');
  let statusUpdatedLabel = null;
  if (platformPulse?.updatedAt) {
    try {
      statusUpdatedLabel = `Updated ${formatDistanceToNowStrict(new Date(platformPulse.updatedAt), { addSuffix: true })}`;
    } catch (_) {
      statusUpdatedLabel = 'Updated moments ago';
    }
  }

  useEffect(() => {
    if (!platformPulse) return;
    if (platformPulse.indicator !== 'operational' && lastStatusIndicatorRef.current !== platformPulse.indicator) {
      track('status_indicator_seen', {
        indicator: platformPulse.indicator,
        label: platformPulse.label
      }).catch(() => {});
      lastStatusIndicatorRef.current = platformPulse.indicator;
    } else if (platformPulse.indicator === 'operational') {
      lastStatusIndicatorRef.current = 'operational';
    }
  }, [platformPulse]);

  const highlightCards = SIDEBAR_FOCUS.highlights.map((highlight) => {
    if (highlight.id === 'lists-ready') {
      return {
        ...highlight,
        value: listStats.readyLabel || highlight.value,
        helper: listStats.helper
      };
    }

    if (highlight.id === 'goal-streak') {
      return {
        ...highlight,
        value: goalStats.summaryLabel || highlight.value,
        helper: goalStats.helper
      };
    }

    return highlight;
  });

  const checklistItems = checklist.length ? checklist : SIDEBAR_FOCUS.checklist;
  const syncMeta = SYNC_STATUS_COPY[syncStatus] || SYNC_STATUS_COPY.synced;
  const statusRows = [
    platformPulse && {
      label: 'Platform status',
      value: platformPulse.label,
      helper: platformPulse.message,
      className: statusSeverityStyles[platformPulse.tone]?.text || 'text-gray-500 dark:text-gray-300'
    },
    {
      label: 'Sync health',
      value: syncMeta.label,
      helper: syncMeta.helper,
      className: syncMeta.className
    },
    {
      label: 'Savings rate',
      value: financeStats.savingsRateLabel,
      helper: financeStats.flowHelper
    },
    {
      label: 'Goals progress',
      value: goalStats.summaryLabel,
      helper: goalStats.completedLabel
    }
  ].filter(Boolean);

  return (
    <div className="h-full flex flex-col finlist-nav-surface bg-white/90 dark:bg-slate-900/80 text-slate-900 dark:text-slate-100" data-testid="primary-sidebar">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-white/40 dark:border-white/10 backdrop-blur">
        <img src="/images/welcome/finlist-logo.png" alt="FinList Command Centre" className="w-8 h-8 rounded-xl shadow-md" />
        <h2 className="text-slate-900 dark:text-white text-base font-semibold tracking-wide">Command Centre</h2>
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
        {SIDEBAR_NAV_GROUPS.map((group) => (
          <section key={group.id}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">{group.title}</p>
                {group.caption && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{group.caption}</p>
                )}
              </div>
            </div>
            <div className="mt-3 space-y-3">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);
                return (
                  <motion.button
                    key={item.id}
                    type="button"
                    onClick={() => handleNavigate(item)}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={cn(
                      'w-full rounded-3xl border border-white/50 dark:border-white/10 bg-white/90 dark:bg-slate-900/70 p-3 text-left transition-all flex items-start gap-3 shadow-finlist-sm backdrop-blur finlist-hover-lift',
                      active ? 'ring-2 ring-brand-300 shadow-finlist-md' : 'hover:border-brand-200/60'
                    )}
                  >
                    <div className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-2xl transition-colors',
                      active ? 'bg-brand-500/15 text-brand-600' : 'bg-slate-100/70 dark:bg-white/5 text-slate-500 dark:text-slate-300'
                    )}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.label}</p>
                        {item.badge && (
                          <span className="text-[10px] uppercase tracking-wide text-brand-600 bg-brand-500/10 rounded-full px-2 py-0.5">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{item.description}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-400" />
                  </motion.button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="border-t border-white/40 dark:border-white/10 px-5 py-4 backdrop-blur">
        {/* Settings/Logout can be placed here */}
      </div>
    </div>
  );
}

function SidebarContent({ children, currentUser, commandData, syncStatus, onLogout }) {
  return (
    <div className="h-full flex flex-col">
      {children ?? (
        <CommandCenterContent
          currentUser={currentUser}
          commandData={commandData}
          syncStatus={syncStatus}
          onLogout={onLogout}
        />
      )}
    </div>
  );
}

export default function PrimarySidebar({ children }) {
  const { currentUser, logout } = useAuth();
  const commandData = useCommandCenterData(currentUser?.uid);
  const syncStatus = useSyncStatus();
  const { isOpen, isPinned, closeSidebar } = useSidebar();
  const shouldRenderOverlay = isOpen && !isPinned;
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const sidebarRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  useOutsideClick(sidebarRef, () => {
    if (isOpen && !isPinned) {
      closeSidebar();
    }
  });

  useEffect(() => {
    if (!shouldRenderOverlay) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [shouldRenderOverlay]);

  useEffect(() => {
    if (!shouldRenderOverlay) return;
    const node = sidebarRef.current;
    if (!node) return;

    const focusableSelectors = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(node.querySelectorAll(focusableSelectors));

    if (focusable.length) {
      firstFocusableRef.current = focusable[0];
      lastFocusableRef.current = focusable[focusable.length - 1];
      focusable[0].focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSidebar();
        return;
      }

      if (event.key !== 'Tab' || focusable.length === 0) {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === firstFocusableRef.current) {
          event.preventDefault();
          lastFocusableRef.current?.focus();
        }
      } else if (document.activeElement === lastFocusableRef.current) {
        event.preventDefault();
        firstFocusableRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [shouldRenderOverlay, closeSidebar]);

  if (!portalTarget) {
    return null;
  }

  const sidebarNode = (
    <AnimatePresence>
      {(isOpen || isPinned) && (
        <>
          {shouldRenderOverlay && (
            <motion.button
              type="button"
              aria-label="Close menu"
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden"
              variants={BACKDROP_VARIANTS}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.2 }}
              onClick={closeSidebar}
            />
          )}

          <motion.aside
            ref={sidebarRef}
            role={shouldRenderOverlay ? 'dialog' : 'complementary'}
            aria-modal={shouldRenderOverlay ? 'true' : undefined}
            aria-label="FinList command center"
            className="fixed z-50 inset-y-0 left-0 w-[86%] max-w-sm finlist-nav-surface border-r border-white/40 dark:border-white/10 shadow-finlist-md flex flex-col lg:w-80"
            variants={SIDEBAR_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
          >
            <SidebarContent currentUser={currentUser} commandData={commandData} syncStatus={syncStatus} onLogout={logout}>
              {children}
            </SidebarContent>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(sidebarNode, portalTarget);
}
